import { SchoolReviewTabsEnum } from "@/enums/aem";
import { getDaysGapString } from "@/methods/utils";
import ApiUnverified from "@/services/apiUnverified";
import {
  SchoolConnectDisplay,
  SchoolMonthlyMeetingDisplay,
  SchoolQueryDisplay,
  SchoolAcademicPlan,
} from "@/types/admin";

type SchoolDataType = SchoolConnectDisplay | SchoolQueryDisplay | SchoolMonthlyMeetingDisplay;

export function groupAndSortBySchool(
  schoolData: Array<SchoolDataType>,
): Array<Array<SchoolDataType>> {
  const schoolMap = new Map<number, Array<SchoolDataType>>();
  schoolData.forEach((item) => {
    const schoolId = item.School.Id;
    if (!schoolMap.has(schoolId)) {
      schoolMap.set(schoolId, []);
    }
    const existingSchool = schoolMap.get(schoolId);
    if (existingSchool) {
      existingSchool.push(item);
    }
  });
  return Array.from(schoolMap.values()).sort((a, b) => {
    return a[0].School.Name.localeCompare(b[0].School.Name);
  });
}

export function getFilteredSchoolsData(
  allSchoolsData: Array<
    Array<SchoolConnectDisplay | SchoolQueryDisplay | SchoolMonthlyMeetingDisplay>
  >,
  searchText: string | null,
) {
  let filteredSchoolsData = allSchoolsData;
  if (searchText) {
    filteredSchoolsData = filteredSchoolsData.filter((school) => {
      return school.some((school) => {
        return school.School.Name.toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }
  return filteredSchoolsData;
}

export async function updateSchoolConnectsForDisplay(
  schoolConnects: Array<SchoolConnectDisplay> | null,
) {
  if (!schoolConnects) {
    return;
  }
  const currentDate = await new ApiUnverified().getServerTime();
  schoolConnects.forEach((connect) => {
    if (connect.AllConnects.length > 0) {
      connect.AllConnects.sort((a, b) => {
        return new Date(b.ContactDate).getTime() - new Date(a.ContactDate).getTime();
      });
      connect.LastConnectDate = connect.AllConnects[0].ContactDate;
      connect.ConnectDaysGap = getDaysGapString(connect.LastConnectDate, currentDate);
    }
  });
  schoolConnects.sort((a, b) => {
    return new Date(b.LastConnectDate).getTime() - new Date(a.LastConnectDate).getTime();
  });
}

export async function updateSchoolQueriesForDisplay(
  schoolQueries: Array<SchoolQueryDisplay> | null,
) {
  if (!schoolQueries) {
    return;
  }
  const currentDate = await new ApiUnverified().getServerTime();
  schoolQueries.forEach((query) => {
    query.DeadlineDaysGap = getDaysGapString(query.QueryDeadline, currentDate);
    query.IsOverdue = new Date(query.QueryDeadline) < currentDate;
  });
  schoolQueries.sort((a, b) => {
    return new Date(b.QueryDeadline).getTime() - new Date(a.QueryDeadline).getTime();
  });
}

export function updateFilteredCards(
  schoolInteractionData: Array<SchoolDataType>,
  selectedStatusFilter: number | null,
  filterType: string,
) {
  if (selectedStatusFilter === null) {
    return schoolInteractionData;
  } else if (filterType === SchoolReviewTabsEnum.SCHOOL_CONNECTS) {
    const connectsData = schoolInteractionData as Array<SchoolConnectDisplay>;
    return connectsData.filter((item) => item.ConnectStatusId === selectedStatusFilter);
  } else if (filterType === SchoolReviewTabsEnum.SCHOOL_QUERIES) {
    const queriesData = schoolInteractionData as Array<SchoolQueryDisplay>;
    return queriesData.filter((item) => item.QueryStatusId === selectedStatusFilter);
  } else if (filterType === SchoolReviewTabsEnum.SCHOOL_MONTHLY_MEETINGS) {
    const meetingsData = schoolInteractionData as Array<SchoolMonthlyMeetingDisplay>;
    return meetingsData.filter((item) => item.MeetingStatusId === selectedStatusFilter);
  }
}

export async function updateSchoolMeetingsForDisplay(
  schoolMeetings: Array<SchoolMonthlyMeetingDisplay> | null,
) {
  if (!schoolMeetings) {
    return;
  }
  schoolMeetings.sort((a, b) => {
    return new Date(b.MeetingDatetime).getTime() - new Date(a.MeetingDatetime).getTime();
  });
}

export function getAcademicFileTypeUrlKey(fileType: string): string {
  const fileTypeKey = fileType.replaceAll(" ", "");
  return `${fileTypeKey}Url`;
}

export function getSchoolAssessmentName(assessmentType: string) {
  return `${assessmentType.replaceAll(" ", "")}Week`;
}

